// uploadUtils.js
import axios from 'axios';
import { ElMessage } from 'element-plus';
import config from '@/config.js';
import { v4 as uuidv4 } from 'uuid';
import Cookies from 'js-cookie';

// 允许的图片类型 (bmp/png/jpeg/jpg/gif)
const ALLOWED_IMAGE_TYPES = [
  'image/bmp',
  'image/png',
  'image/jpeg',
  'image/gif'
];

// 允许的视频类型 (mp4)
const ALLOWED_VIDEO_TYPES = [
  'video/mp4'
];

// 允许的音频类型 (mp3/wma/wav/amr)
const ALLOWED_AUDIO_TYPES = [
  'audio/mpeg', // mp3
  'audio/x-ms-wma', // wma
  'audio/wav', // wav
  'audio/amr' // amr
];

// 允许的文档类型 (csv/txt)
const ALLOWED_DOCUMENT_TYPES = [
  'text/csv',
  'text/plain',
  'application/vnd.ms-excel', // 某些CSV文件的MIME类型
  'application/csv'
];

// 最大文件大小限制 (单位: 字节)
const MAX_FILE_SIZES = {
  image: 20 * 1024 * 1024, // 20MB
  video: 100 * 1024 * 1024, // 100MB
  audio: 20 * 1024 * 1024, // 20MB
  document: 100 * 1024 * 1024 // 100MB
};

/**
 * 获取上传签名
 * @param {string} type 文件类型（如 'video', 'image', 'audio', 'document' 等）
 * @returns {Promise<Object>} 返回签名数据
 */
export const getUploadSignature = async (type = 'file') => {
  try {
    const userInfo = JSON.parse(Cookies.get('pc_user_info') || '{}');
    // 验证必要字段存在
    if (!userInfo.id) {
      throw new Error('用户信息中缺少商户ID');
    }
    const response = await axios.post(
      `${config.BASE_URL}/shop/api/upload_signature.php`,
      {
        type, // 上传类型
        id: userInfo.store_id // 使用代理ID作为店铺标识
      },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    // 检查接口返回的数据
    if (response.data && response.data.success) {
      return response.data;
    } else {
      // 如果接口返回 success 为 false，抛出错误
      throw new Error(response.data.error || "获取上传签名失败");
    }
  } catch (error) {
    console.error("获取上传签名失败:", error);
    throw error;
  }
};

/**
 * 获取文件类型分类
 * @param {string} mimeType MIME类型
 * @returns {string} 返回文件类型分类 ('image', 'video', 'audio', 'document')
 */
const getFileCategory = (mimeType) => {
  if (ALLOWED_IMAGE_TYPES.includes(mimeType)) return 'image';
  if (ALLOWED_VIDEO_TYPES.includes(mimeType)) return 'video';
  if (ALLOWED_AUDIO_TYPES.includes(mimeType)) return 'audio';
  if (ALLOWED_DOCUMENT_TYPES.includes(mimeType)) return 'document';
  return 'file';
};

/**
 * 获取文件扩展名
 * @param {string} fileName 文件名
 * @returns {string} 文件扩展名
 */
const getFileExtension = (fileName) => {
  return fileName.split('.').pop().toLowerCase();
};

/**
 * 验证文档文件
 * @param {File} file 文件对象
 * @returns {boolean} 是否有效
 */
const validateDocumentFile = (file) => {
  const extension = getFileExtension(file.name);
  const allowedExtensions = ['.csv', '.txt'];

  // 检查扩展名
  if (!allowedExtensions.includes('.' + extension)) {
    return false;
  }

  // 检查MIME类型
  if (!ALLOWED_DOCUMENT_TYPES.includes(file.type)) {
    // 如果MIME类型不在允许列表中，但扩展名正确，也允许上传
    // 因为某些CSV文件的MIME类型可能不正确
    return true;
  }

  return true;
};

/**
 * 阿里云OSS上传
 * @param {File} file 文件
 * @param {Object} signatureData 签名数据
 * @returns {Promise<Object>} 上传结果
 */
const uploadToAliyunOSS = async (file, signatureData) => {
  const uniqueFileName = `${uuidv4()}.${getFileExtension(file.name)}`;

  const formData = new FormData();
  formData.append("key", `${signatureData.folder}${uniqueFileName}`);
  formData.append("policy", signatureData.policy);
  formData.append("OSSAccessKeyId", signatureData.accessKeyId);
  formData.append("signature", signatureData.signature);
  formData.append("success_action_status", "200");
  formData.append("file", file);

  const response = await axios.post(signatureData.upload_url, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });

  if (response.status === 200) {
    const fileUrl = `https://${signatureData.bucket}.${signatureData.endpoint}/${signatureData.folder}${uniqueFileName}`;
    return {
      success: true,
      url: fileUrl,
      size: file.size,
      type: file.type,
      category: getFileCategory(file.type),
      name: file.name
    };
  } else {
    throw new Error("阿里云OSS上传失败");
  }
};

/**
 * 腾讯云COS上传
 * @param {File} file 文件
 * @param {Object} signatureData 签名数据
 * @returns {Promise<Object>} 上传结果
 */
const uploadToTencentCOS = async (file, signatureData) => {
  const uniqueFileName = `${uuidv4()}.${getFileExtension(file.name)}`;
  const key = `${signatureData.folder}${uniqueFileName}`;

  const formData = new FormData();
  formData.append("key", key);
  formData.append("policy", signatureData.policy);
  formData.append("q-sign-algorithm", "sha1");
  formData.append("q-ak", signatureData.secretId);
  formData.append("q-sign-time", signatureData.keyTime);
  formData.append("q-key-time", signatureData.keyTime);
  formData.append("q-signature", signatureData.signature);
  formData.append("success_action_status", "200");

  // 重要：文件必须最后添加
  formData.append("file", file);

  try {
    const response = await axios.post(signatureData.upload_url, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      // 添加超时设置
      timeout: 30000,
    });

    if (response.status === 200 || response.status === 204) {
      const fileUrl = `${signatureData.upload_url}/${key}`;
      return {
        success: true,
        url: fileUrl,
        size: file.size,
        type: file.type,
        category: getFileCategory(file.type),
        name: file.name
      };
    } else {
      throw new Error(`腾讯云COS上传失败，状态码: ${response.status}`);
    }
  } catch (error) {
    console.error('腾讯云上传错误:', error);
    if (error.response) {
      console.error('错误响应:', error.response.data);
    }
    throw new Error("腾讯云COS上传失败: " + error.message);
  }
};

/**
 * 七牛云上传
 * @param {File} file 文件
 * @param {Object} signatureData 签名数据
 * @returns {Promise<Object>} 上传结果
 */
const uploadToQiniu = async (file, signatureData) => {
  const uniqueFileName = `${uuidv4()}.${getFileExtension(file.name)}`;
  const key = `${signatureData.folder}${uniqueFileName}`;

  const formData = new FormData();
  formData.append("key", key); // 指定文件 key
  formData.append("token", signatureData.token);
  formData.append("file", file);

  try {
    const response = await axios.post(signatureData.upload_url, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      timeout: 30000,
    });

    if (response.status === 200) {
      // 七牛云返回的数据在 response.data 中
      const result = response.data;
      const fileUrl = `${signatureData.domain}/${result.key || key}`;

      return {
        success: true,
        url: fileUrl,
        size: file.size,
        type: file.type,
        category: getFileCategory(file.type),
        name: file.name,
        hash: result.hash,
        key: result.key
      };
    } else {
      throw new Error(`七牛云上传失败，状态码: ${response.status}`);
    }
  } catch (error) {
    console.error('七牛云上传错误:', error);
    if (error.response) {
      console.error('错误响应:', error.response.data);
    }
    throw new Error("七牛云上传失败: " + (error.response?.data?.error || error.message));
  }
};


/**
 * 本地存储上传
 * @param {File} file 文件
 * @param {Object} signatureData 签名数据
 * @returns {Promise<Object>} 上传结果
 */
const uploadToLocal = async (file, signatureData) => {
  const uniqueFileName = `${uuidv4()}.${getFileExtension(file.name)}`;
  const key = `${signatureData.folder}${uniqueFileName}`;

  const formData = new FormData();
  formData.append("file", file);
  formData.append("key", key);
  formData.append("folder", signatureData.folder);

  try {
    const response = await axios.post(signatureData.upload_url, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      timeout: 30000,
    });

    if (response.data && response.data.success) {
      return {
        success: true,
        url: response.data.url,
        size: file.size,
        type: file.type,
        category: getFileCategory(file.type),
        name: file.name,
        key: response.data.key
      };
    } else {
      throw new Error(response.data.error || "本地存储上传失败");
    }
  } catch (error) {
    console.error('本地存储上传错误:', error);
    if (error.response) {
      console.error('错误响应:', error.response.data);
    }
    throw new Error("本地存储上传失败: " + (error.response?.data?.error || error.message));
  }
};

/**
 * 上传文件到云存储
 * @param {File} file 要上传的文件
 * @param {Object} signatureData 签名数据
 * @returns {Promise<{ success: boolean, url: string, size: number }>} 返回上传结果
 */
export const uploadToOSS = async (file, signatureData) => {
  const fileCategory = getFileCategory(file.type);

  // 验证文件类型
  let allowedTypes;
  switch (fileCategory) {
    case 'image':
      allowedTypes = ALLOWED_IMAGE_TYPES;
      break;
    case 'video':
      allowedTypes = ALLOWED_VIDEO_TYPES;
      break;
    case 'audio':
      allowedTypes = ALLOWED_AUDIO_TYPES;
      break;
    case 'document':
      // 对于文档文件，使用扩展名和MIME类型双重验证
      if (!validateDocumentFile(file)) {
        throw new Error(`不支持的文件类型: ${file.type}，仅支持 CSV、TXT 格式`);
      }
      allowedTypes = ALLOWED_DOCUMENT_TYPES;
      break;
    default:
      throw new Error(`不支持的文件类型: ${file.type}`);
  }

  // 对于非文档文件，进行严格的MIME类型验证
  if (fileCategory !== 'document' && !allowedTypes.includes(file.type)) {
    throw new Error(`不支持的文件类型: ${file.type}，仅支持 ${allowedTypes.join(', ')}`);
  }

  // 验证文件大小
  const maxSize = MAX_FILE_SIZES[fileCategory];
  if (file.size > maxSize) {
    throw new Error(`文件大小超过限制 (${maxSize / 1024 / 1024}MB): ${(file.size / 1024 / 1024).toFixed(2)}MB`);
  }

  // 根据云服务商选择上传方法
  const cloudProvider = signatureData.cloud_provider || 'aliyun';

  switch (cloudProvider) {
    case 'tencent':
      return await uploadToTencentCOS(file, signatureData);
    case 'qiniu':
      return await uploadToQiniu(file, signatureData);
    case 'local':
      return await uploadToLocal(file, signatureData);
    case 'aliyun':
    default:
      return await uploadToAliyunOSS(file, signatureData);
  }
};

/**
 * 上传文件前的校验
 * @param {File} file 要上传的文件
 * @param {Object} options 校验选项
 * @param {number} options.maxSize 文件最大大小（MB）
 * @param {string} options.type 文件类型 ('image', 'video', 'audio', 'document')
 * @returns {Promise<boolean>} 返回校验结果
 */
export const beforeUpload = async (file, options = {}) => {
  const { maxSize, type } = options;
  const fileCategory = type || getFileCategory(file.type);

  // 确定允许的文件类型
  let allowedTypes;
  switch (fileCategory) {
    case 'image':
      allowedTypes = ALLOWED_IMAGE_TYPES;
      break;
    case 'video':
      allowedTypes = ALLOWED_VIDEO_TYPES;
      break;
    case 'audio':
      allowedTypes = ALLOWED_AUDIO_TYPES;
      break;
    case 'document':
      // 对于文档文件，使用扩展名验证
      if (!validateDocumentFile(file)) {
        ElMessage.error(`不支持的文件类型: ${file.type}，仅支持 CSV、TXT 格式`);
        return false;
      }
      allowedTypes = ALLOWED_DOCUMENT_TYPES;
      break;
    default:
      ElMessage.error(`不支持的文件类型: ${file.type}`);
      return false;
  }

  // 对于非文档文件，进行严格的MIME类型验证
  if (fileCategory !== 'document' && !allowedTypes.includes(file.type)) {
    ElMessage.error(`不支持的文件类型: ${file.type}，仅支持 ${allowedTypes.join(', ')}`);
    return false;
  }

  // 校验文件大小
  const maxAllowedSize = maxSize
    ? Math.min(maxSize * 1024 * 1024, MAX_FILE_SIZES[fileCategory])
    : MAX_FILE_SIZES[fileCategory];

  if (file.size > maxAllowedSize) {
    ElMessage.error(`文件大小不能超过 ${(maxAllowedSize / 1024 / 1024).toFixed(2)}MB`);
    return false;
  }

  return true;
};

/**
 * 获取视频/音频时长
 * @param {File} file 视频/音频文件
 * @returns {Promise<number>} 返回视频/音频时长（秒）
 */
export const getVideoDuration = (file) => {
  return new Promise((resolve, reject) => {
    const media = document.createElement(
      file.type.startsWith('video/') ? 'video' :
        file.type.startsWith('audio/') ? 'audio' :
          'video'
    );
    media.src = URL.createObjectURL(file);
    media.onloadedmetadata = () => {
      resolve(media.duration);
      URL.revokeObjectURL(media.src); // 释放内存
    };
    media.onerror = () => {
      URL.revokeObjectURL(media.src); // 释放内存
      reject(new Error("无法读取文件时长"));
    };
  });
};

/**
 * 获取文件缩略图（适用于图片和视频）
 * @param {File} file 文件
 * @returns {Promise<string>} 返回缩略图URL
 */
export const getFileThumbnail = (file) => {
  return new Promise((resolve, reject) => {
    if (file.type.startsWith('image/')) {
      // 图片文件直接生成缩略图
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    } else if (file.type.startsWith('video/')) {
      // 视频文件获取第一帧作为缩略图
      const video = document.createElement('video');
      const canvas = document.createElement('canvas');
      video.src = URL.createObjectURL(file);

      video.addEventListener('loadedmetadata', () => {
        video.currentTime = Math.min(1, video.duration);
      });

      video.addEventListener('seeked', () => {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL('image/jpeg'));
        URL.revokeObjectURL(video.src);
      });

      video.addEventListener('error', () => {
        URL.revokeObjectURL(video.src);
        reject(new Error("无法生成视频缩略图"));
      });
    } else {
      // 对于文档文件，返回null或默认图标
      resolve(null);
    }
  });
};

/**
 * 专门用于上传数据文件的方法
 * @param {File} file 数据文件（CSV/TXT）
 * @returns {Promise<{ success: boolean, url: string, size: number }>} 返回上传结果
 */
export const uploadDataFile = async (file) => {
  try {
    // 验证文件类型
    const allowedExtensions = ['.csv', '.txt'];
    const extension = '.' + getFileExtension(file.name);

    if (!allowedExtensions.includes(extension)) {
      throw new Error(`不支持的文件类型，仅支持 ${allowedExtensions.join(', ')} 格式`);
    }

    // 验证文件大小
    const maxSize = MAX_FILE_SIZES.document;
    if (file.size > maxSize) {
      throw new Error(`文件大小超过限制 (${maxSize / 1024 / 1024}MB): ${(file.size / 1024 / 1024).toFixed(2)}MB`);
    }

    // 获取签名并上传
    const signature = await getUploadSignature('document');
    const result = await uploadToOSS(file, signature);

    return result;
  } catch (error) {
    console.error('数据文件上传失败:', error);
    throw error;
  }
};